import React from 'react';

import { Divider } from 'antd';
import {
  Avatar,
  AvatarShape,
  Column,
  LoadingOutlined,
  Pagination,
  Popover,
  PopoverPlacement,
  Row,
  Text,
  Tooltip,
} from 'farmhub-ui-core';
import { useSelector } from 'react-redux';

import { RootState, useAppSelector } from 'app';
import { Empty, SearchTable, dataEmpty } from 'components';
import { OrderFilledIcon } from 'components/Icon';
import AssignerItemSkeleton from 'components/Skeleton/AssignerItemSkeleton';
import { employee } from 'containers/WarehouseOperation/OrderOperation/Order/Overview/common/query';
import { useClickOutSide, useFetchTableData, useQueryParam } from 'hooks';
import { findEmployeeForTableList } from 'slice';
import { theme } from 'styled';
import {
  formatAppearPhoneNumber,
  getDummyArray,
  renderEmployeeRole,
} from 'utils';

export const option = {
  isVisible: false,
  whiteList: ['order', 'take', 'page', 'where'],
};

import { Content, IconWrapper, Item, ResultWrapper } from './styles';

type ChooseEmployeePopoverProps = {
  isAssigning: boolean;
  orderTaskID: string;
  assignee?: any;
  onAssignee: (orderTaskID: string, assigneeID: string) => Promise<void>;
};

const ChooseEmployeePopover = ({
  isAssigning,
  orderTaskID,
  assignee,
  onAssignee,
}: ChooseEmployeePopoverProps) => {
  const userID = useSelector((state: RootState) => state.auth.item?.id);
  const entityID = useAppSelector(state => state.account.item?.id);
  const [visible, setVisible] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [query, setQuery] = React.useState({
    ...employee,
    'where.ownerID': entityID,
    page: 1,
    take: 10,
  });

  useClickOutSide(containerRef, () => {
    setVisible(false);
  });

  useQueryParam({ query, option, deps: [query] });

  const {
    isLoading,
    data: employeeList,
    setData: setEmployeeList,
    onReFreshList: handleRefreshList,
    pagination,
    setPagination,
  } = useFetchTableData({
    action: findEmployeeForTableList,
    query,
    isVisible: visible,
    deps: [query],
  });

  const handleFilterFormChange = e => {
    if (e.isObject) {
      return;
    }

    const key = Object.keys(e)[0];
    const value = Object.values(e)[0];

    if (value === 'all') {
      delete query[key];
      setQuery({ ...query });
    } else {
      if (key === 'where.user.displayName.fs') {
        setQuery({ ...query, ...e, page: 1 });
      } else {
        setQuery({ ...query, ...e });
      }
    }
  };

  const handlePaginationChange = (page: any, pageSize: any) => {
    setQuery({
      ...query,
      page,
      take: pageSize,
    });
  };

  const content = (
    <Content ref={containerRef}>
      <SearchTable
        height="xs"
        placeholder="Tìm kiếm theo tên"
        width="100%"
        onChange={e =>
          handleFilterFormChange({
            'where.user.displayName.fs': e.target.value,
          })
        }
      />
      <ResultWrapper>
        {isLoading ? (
          getDummyArray(5).map((item: any) => <AssignerItemSkeleton />)
        ) : employeeList.length > 0 ? (
          employeeList.map((item: any) => (
            <Item
              onClick={() => {
                if (item.user?.id && assignee?.id != item.user.id) {
                  onAssignee(orderTaskID, item.user.id);
                }

                setVisible(false);
              }}
            >
              <Row alignItems="center" styles={{ gap: 8, padding: '6px 0px' }}>
                <Avatar
                  shape={AvatarShape.Circle}
                  size={32}
                  src={item.user.avatar}
                />
                <Column styles={{ gap: 0 }}>
                  <Text color="primary" size="xxs" weight="bold">
                    {item.user.displayName}
                  </Text>
                  <Row>
                    <Text color="main" size="tiny">
                      {formatAppearPhoneNumber(item.user?.phoneNumber)}
                    </Text>
                    <Divider type="vertical" />
                    <Text color="main" size="tiny">
                      {renderEmployeeRole(item.type)}
                    </Text>
                  </Row>
                </Column>
              </Row>
            </Item>
          ))
        ) : (
          <Empty
            description="Hiện tại không nhân viên"
            img={dataEmpty.ACCOUNT}
          />
        )}
      </ResultWrapper>

      {employeeList.length > 0 && (
        <Row justifyContent="flex-end">
          <Pagination
            size="small"
            {...pagination}
            pageSizeOptions={['10']}
            onChange={handlePaginationChange}
          />
        </Row>
      )}
    </Content>
  );

  return (
    <Row width="fit-content">
      <Popover
        containerId="container"
        content={content}
        placement={PopoverPlacement.BottomRight}
        trigger="click"
        visible={visible}
      >
        <Row
          justifyContent="center"
          styles={{ cursor: 'pointer' }}
          width="fit-content"
          onClick={() => setVisible(true)}
        >
          {assignee ? (
            <Row
              alignItems="center"
              justifyContent="center"
              styles={{ gap: 4 }}
              width="fit-content"
            >
              {isAssigning ? (
                <LoadingOutlined
                  spin
                  style={{ fontSize: 18, color: theme.colors.primary.main }}
                />
              ) : (
                <Avatar
                  shape={AvatarShape.Circle}
                  size={18}
                  src={assignee.avatar}
                />
              )}
              <Tooltip title={assignee.displayName}>
                <Text
                  lineNumber={1}
                  size="xxs"
                  styles={{ maxWidth: '64px' }}
                  weight="medium"
                >
                  {
                    assignee.displayName.split(' ')[
                      assignee.displayName.split(' ').length - 1
                    ]
                  }
                </Text>
              </Tooltip>
            </Row>
          ) : (
            <Row
              alignItems="center"
              justifyContent="center"
              styles={{ gap: 4 }}
              width="fit-content"
            >
              {isAssigning ? (
                <LoadingOutlined
                  spin
                  style={{ fontSize: 18, color: theme.colors.primary.main }}
                />
              ) : (
                <IconWrapper>
                  <OrderFilledIcon height={12} width={12} />
                </IconWrapper>
              )}
              <Text size="xxs" weight="medium">
                Phân công
              </Text>
            </Row>
          )}

          <Divider type="vertical" />
        </Row>
      </Popover>
      <Row
        styles={{ cursor: 'pointer' }}
        width="fit-content"
        onClick={() => {
          if (userID && assignee?.id != userID) {
            onAssignee(orderTaskID, userID);
          }
        }}
      >
        <Text
          color="main"
          size="xxs"
          styles={{ display: 'block', flexShrink: 0 }}
          weight="medium"
        >
          Chọn tôi
        </Text>
      </Row>
    </Row>
  );
};

export default ChooseEmployeePopover;
